// WhyRecommendationInfo — universal (i) info popover next to the tier
// badge on both Admin Workbench and Client surfaces (Reference Library,
// Client Dashboard). Feb 2026 · user request: "let us have a rationale
// just in front of the recommendation … why we have considered this in
// high priority, why we have considered this in medium, or monitor low
// priority, not suitable." Same shape on both sides so there is one
// consistent 'why' surface across the app.
//
// Rendering logic:
//   1. Divergent (ai_concern.direction === 'upgrade' / 'downgrade'):
//      green/amber colours; header "Why elevated above math band" /
//      "Why not in top tier"; body = math vs AI comparison line +
//      Claude's rationale + Considerations/Key risks.
//   2. Agreeing (ai_concern is null but a rationale exists on the
//      assessment): neutral slate colours; header "Why <tier>";
//      body = weighted-score line + tier_rationale + tier_considerations.
//   3. Off-strategy (recommendation is 'Not Suitable' and an off_strategy
//      reason is stored): rose colours; header "Why Not Suitable";
//      body = weighted line + off_strategy_reason + fit-screen note.
//   4. Not Assessed (no recommendation, no rationale): renders nothing.
import React, { useState, useRef } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

export default function WhyRecommendationInfo({ asset, testIdPrefix = 'why-rec' }) {
  const [open, setOpen] = useState(false);
  // Jun 2026 · the table wrapper's overflow-x-auto clips absolutely
  // positioned children, so the popover is FIXED at the button's live
  // viewport coordinates instead (user report: "the box can't be seen").
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);
  if (!asset) return null;

  const ac = asset.ai_concern || {};
  const rec = asset.recommendation;
  const weighted =
    asset.weighted_avg_score
    ?? (asset.composite_score != null ? asset.composite_score / 9 : null);
  const eaId = asset.ea_id || asset.id || '';
  const offStrategy = asset.off_strategy_reason;
  const tierRationale = asset.tier_rationale;
  const tierConsiderations = Array.isArray(asset.tier_considerations)
    ? asset.tier_considerations : [];

  // ─── Determine mode ─────────────────────────────────────────────
  const isDivergent = !!(ac && ac.direction);
  const isUp = ac?.direction === 'upgrade';
  const isOffStrategy = rec === 'Not Suitable' && offStrategy;

  // Skip render if nothing to say at all.
  if (!isDivergent && !isOffStrategy && !tierRationale && !rec) return null;
  if (!rec) return null; // no tier yet → nothing to justify

  // ─── Style palette per mode ─────────────────────────────────────
  let chipCls = 'text-slate-500 hover:text-slate-800 hover:bg-slate-100';
  let popoverCls = 'border-slate-200';
  let eyebrowCls = 'text-slate-600';
  let eyebrowLabel = `Why ${rec}`;
  if (isDivergent) {
    chipCls = isUp
      ? 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100'
      : 'text-amber-700 hover:text-amber-900 hover:bg-amber-100';
    popoverCls = isUp ? 'border-emerald-200' : 'border-amber-200';
    eyebrowCls = isUp ? 'text-emerald-700' : 'text-amber-700';
    eyebrowLabel = isUp ? 'Why elevated above math band' : 'Why not in top tier';
  } else if (isOffStrategy) {
    chipCls = 'text-rose-700 hover:text-rose-900 hover:bg-rose-100';
    popoverCls = 'border-rose-200';
    eyebrowCls = 'text-rose-700';
    eyebrowLabel = 'Why Not Suitable';
  }

  // ─── Body composition ───────────────────────────────────────────
  const scoreLine = weighted != null
    ? `Weighted ${weighted.toFixed(2)}/5${ac.math_recommendation ? ` · math band = ${ac.math_recommendation}` : ''} · AI assigned = ${rec}`
    : `AI assigned = ${rec}`;

  // Prefer ai_concern's rationale (from Claude's executive summary) for
  // divergent rows; otherwise fall back to the row-level tier_rationale
  // pulled off the assessment on the backend. Off-strategy rationale
  // wins when the row failed the fit screen.
  const rationaleText = isOffStrategy
    ? offStrategy
    : (isDivergent ? ac.rationale : null)
      || tierRationale
      || (isDivergent ? '' : `${rec} — click Deep Dive for the full assessment.`);

  const considerations = isDivergent && Array.isArray(ac.key_risks) && ac.key_risks.length
    ? ac.key_risks
    : tierConsiderations;

  const considerationsHeader = isDivergent
    ? (isUp ? 'Considerations' : 'Key risks')
    : (rec === 'Not Suitable' || rec === 'Low Priority' || rec === 'Monitor' || rec === 'Could Not Assess')
      ? 'Key risks'
      : 'Considerations';

  return (
    <span className="relative inline-block">
      <button
        type="button"
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            const below = window.innerHeight - r.bottom;
            setPos({
              top: below < 260 ? Math.max(8, r.top - 250) : r.bottom + 4,
              right: Math.max(8, window.innerWidth - r.right),
            });
          }
          setOpen((o) => !o);
        }}
        className={`inline-flex items-center justify-center p-0.5 rounded-full transition-colors ${chipCls}`}
        title={`Why this asset was placed in "${rec}"`}
        data-testid={`${testIdPrefix}-info-${eaId}`}
        aria-expanded={open}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-backdrop-${eaId}`}
          />
          <div
            className={`fixed w-96 z-50 bg-white border ${popoverCls} rounded-lg shadow-xl p-3 text-left whitespace-normal break-words max-h-60 overflow-y-auto`}
            style={{ top: pos?.top ?? 0, right: pos?.right ?? 0 }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testIdPrefix}-popover-${eaId}`}
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className={`text-[10px] uppercase tracking-wide font-semibold inline-flex items-center gap-1 ${eyebrowCls}`}>
                <AlertTriangle className="w-3 h-3" />
                {eyebrowLabel}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
                data-testid={`${testIdPrefix}-close-${eaId}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mb-2">{scoreLine}</div>
            {rationaleText && (
              <p className="text-xs text-slate-700 leading-snug mb-2">{rationaleText}</p>
            )}
            {considerations.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-1">
                  {considerationsHeader}
                </div>
                <ul className="list-disc pl-4 text-[11px] text-slate-700 space-y-0.5">
                  {considerations.slice(0, 4).map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}
